<template>
  <div>
    <div class="w-full overflow-hidden bg-white xl:flex xl:justify-end xl:space-x-4">
      <div class="w-full">
        <div class="flex-grow px-2">
          <h3 class="text-2xl font-extrabold text-gray-900 sm:text-3xl">
            {{ upgradePrice.nickname }}
          </h3>
          <p class="mt-1 text-base text-gray-500">
            {{ upgradePrice.description }}
          </p>
          <section class="flex justify-between">
            <div class="mt-8">
              <h4 class="text-sm font-medium text-gray-900">Features</h4>
              <!-- eslint-disable-next-line max-len -->
              <ul class="mt-2 space-y-5 lg:space-y-0 lg:grid lg:grid-cols-1 lg:gap-x-8 lg:gap-y-5">
                <li
                  v-for="(feature, idx) in upgradePrice.features"
                  :key="idx"
                  class="flex items-start lg:col-span-1"
                >
                  <div class="flex-shrink-0">
                    <!-- Heroicon name: solid/check-circle -->
                    <svg class="h-5 w-5 text-green-600" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                      <!-- eslint-disable-next-line max-len -->
                      <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clip-rule="evenodd" />
                    </svg>
                  </div>
                  <p class="ml-3 text-sm text-gray-700">
                    {{ feature }}
                  </p>
                </li>
              </ul>
            </div>
            <div class="mt-8" v-if="upgradePrice.tiers">
              <h4 class="text-sm font-medium text-gray-900">Pricing</h4>
              <ul class="mt-2 space-y-5 lg:space-y-0 lg:grid lg:grid-cols-1 lg:gap-x-8 lg:gap-y-5">
                <li
                  v-for="(tier, idx) in upgradePrice.tiers"
                  :key="idx"
                  class="flex items-center lg:col-span-1"
                >
                  <p class="text-sm text-gray-700">
                    {{ tier.unit_amount / 100 | dollar }} per user
                    <span v-if="tier.up_to">up to {{ tier.up_to }} users</span>
                    <span v-else>after that</span>
                  </p>
                </li>
              </ul>
            </div>
          </section>
        </div>
        <!-- eslint-disable-next-line max-len -->
        <div class="py-8 px-6 w-full text-center">
          <div v-if="upgradePrice.billing_scheme === 'tiered'">
            <price-calculator :price="upgradePrice" v-model="qty"></price-calculator>
          </div>
          <div v-else-if="upgradePrice.billing_scheme === 'per_unit'">
            <!-- eslint-disable-next-line max-len -->
            <div class="mt-4 flex items-baseline justify-center text-5xl font-extrabold text-gray-900 leading-none">
              <span>
                ${{ (upgradePrice.unit_amount / 100).toFixed(0) }}
              </span>
              <span class="ml-3 text-xl font-medium text-gray-500">
                / month
              </span>
              <span class="ml-3 text-xl font-medium text-gray-500">(inc GST)</span>
            </div>
          </div>
        </div>
      </div>
      <div class="mt-4 w-full flex flex-col justify-between space-y-8">
        <div class="bg-gray-100 p-4 rounded">
          <div class="text-sm text-gray-600 space-y-2">
            <!-- eslint-disable max-len -->
            <p>You will be able to use the new features immediately but won't pay anything until your next monthly invoice.</p>
            <p>On that invoice, you'll pay the new monthly fee for your upgraded subscription plus a once off adjustment fee to reflect your upgrade for the rest of this month.</p>
            <p>Click <span class="font-medium">preview</span> to see the total amount that will appear on your next invoice.</p>
            <!-- eslint-enable max-len -->
          </div>
          <div v-if="previewed">
            <ul class="mt-4 text-xs text-gray-600">
              <li
                v-for="(item,idx) in update.items"
                :key="idx"
                class="py-1"
              >
                <dl class="flex justify-between space-x-4">
                  <dd class="leading-tight">{{ item.description }}</dd>
                  <dt>{{ item.amount / 100 | dollar }}</dt>
                </dl>
              </li>
              <li class="py-2 border-t border-t-gray-300 font-bold">
                <dl class="flex justify-between">
                  <dd>Total</dd>
                  <dt>{{ update.amount / 100 | dollar }}</dt>
                </dl>
              </li>
            </ul>
            <p class="text-xs text-right">All prices inc GST</p>
          </div>

        </div>
        <div class="flex justify-end items-end space-x-2">
          <div class="relative">
            <m-button
              v-if="previewed"
              class="w-full"
              :id="`select-price-${upgradePrice.id}`"
              @click="handleUpgrade"
            >
              Upgrade
            </m-button>
            <m-button
              v-else
              class="w-full"
              :id="`select-price-${upgradePrice.id}`"
              @click="handlePreview"
            >
              Preview
            </m-button>
            <div
              class="absolute inset-0 flex items-center justify-center bg-blue-900 rounded"
              v-if="loading"
            >
              <div class="h-4 w-4">
                <m-loader class="text-white"></m-loader>
              </div>
            </div>
          </div>
          <div class="">
            <m-button
              class="w-full"
              variant="secondary"
              @click="handleCancel"
            >
              Cancel
            </m-button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { API, ENDPOINTS } from '@/app/services/api-url';
import PriceCalculator from '@/app/modules/account/components/subscribe/price-info/price-calculator/price-calulator.vue';

export default {
  name: 'account-app-upgrade',
  components: {
    PriceCalculator,
  },
  props: {
    subscription: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      qty: 1,
      loading: false,
      previewed: false,
      update: null,
    };
  },
  computed: {
    product() {
      return this.$store.getters['billing/getPriceByProduct'](this.subscription.appName);
    },
    upgradePrice() {
      return this.product ? this.product.prices.filter(price => price.level === 'PROFESSIONAL')[0] : [];
    },
  },
  methods: {
    handleCancel() {
      this.loading = false;
      this.previewed = false;
      this.update = null;
      this.$emit('close');
    },
    updateSubscription(preview = true) {
      this.loading = true;
      return API.post(`${ENDPOINTS.POST_SUBSCRIPTION}/${this.subscription.subscriptionID}`, {
        accountID: this.subscription.accountID,
        priceID: this.upgradePrice.id,
        previewInvoice: preview,
      });
    },
    handlePreview() {
      this.updateSubscription()
        .then((res) => {
          this.previewed = true;
          this.update = res.data.data.updatedSubscriptionResponse;
          this.loading = false;
        })
        .catch(err => console.log(err));
    },
    handleUpgrade() {
      this.updateSubscription(false)
        .then(() => {
          this.$mixpanel.track('successfulUpgrade', {
            upgradedApp: this.upgradePrice.id,
            accountName: this.$store.getters['account/getActiveAccount'].name,
          });
          this.$store.dispatch('billing/getSubscriptions');
          this.previewed = false;
          this.update = null;
          this.loading = false;
          this.$emit('close');
        })
        .catch(err => console.log(err));
    },
  },
};
</script>
